/**
 * Circuit breaker for the satellite -> primary auth-sync bounce.
 *
 * A satellite auth page (HLX host) redirects to the primary (MCV host) to run
 * Clerk sign-in, then Clerk returns the browser here to sync the session. When
 * that cross-site session cannot settle — e.g. Safari Private Browsing / ITP
 * blocking the third-party `*.accounts.dev` cookie on a pk_test instance — the
 * round trip lands us back signed-out and we bounce again, forever. This is the
 * MCV-1445 "Double re-directs between sites" failure class; the original guard
 * only paused on `__clerk_handshake` (the production protocol) and is blind to
 * the development sync protocol (`__clerk_synced` / `__clerk_db_jwt`).
 *
 * We can't reliably detect "still signed out after a sync" from the URL —
 * clerk-js strips its markers via `history.replaceState`, which React never
 * observes (the same reason MCV-1442 stopped trusting `location.search`). So we
 * count the bounces in `sessionStorage` (per browsing context, survives the
 * same-origin reloads of the loop) and report `blocked` once they exceed a cap,
 * letting the caller stop redirecting and show a recoverable message.
 *
 * [MCV-1789] The counter is built by `createSyncAttemptGuard` so the same breaker
 * can also run on the primary (MCV) origin, where the loop is driven by
 * `AuthenticatedSignInRoute` rather than by the satellite.
 */

// A settled sign-in needs one, occasionally two, satellite->primary bounces, so
// blocking on the 4th within an episode never trips a legitimate flow.
export const MAX_SATELLITE_SYNC_ATTEMPTS = 4;

// A gap longer than this means a fresh, user-driven attempt rather than the
// tight machine loop, so the counter starts a new episode instead of punishing
// a later sign-in. Generous enough that even a slow loop keeps accumulating.
export const SATELLITE_SYNC_EPISODE_GAP_MS = 5 * 60_000;

type Attempts = { count: number; lastAttemptAt: number };

export type SyncAttemptGuard = {
  /**
   * Record one bounce and return the running count for the current episode. A
   * bounce that follows a long quiet gap starts a fresh episode at 1.
   */
  record: (now?: number) => number;
  /** True once bounces in the current episode have hit the cap. */
  isBlocked: (now?: number) => boolean;
  /** Clear the counter. */
  reset: () => void;
};

// Storage layer: sessionStorage primary, first-party session cookie fallback.
//
// The breaker must keep counting across the loop's full-page reloads. If it only
// used sessionStorage and that threw (some privacy-locked / sandboxed configs
// reject Web Storage access), every read would return null and every write would
// be discarded — so each bounce would count as attempt 1 forever, the block
// would never trip, and the satellite would redirect indefinitely. That is the
// exact failure the breaker exists to stop, so it must fail CLOSED: fall back to
// a cookie on the satellite origin, which survives the same reloads.
const COOKIE_MAX_AGE_S = Math.ceil(SATELLITE_SYNC_EPISODE_GAP_MS / 1000);

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseAttempts = (raw: string | null): Attempts | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Attempts>;
    if (typeof parsed?.count !== 'number' || typeof parsed?.lastAttemptAt !== 'number') {
      return null;
    }
    return { count: parsed.count, lastAttemptAt: parsed.lastAttemptAt };
  } catch {
    return null;
  }
};

const isStale = (attempts: Attempts, now: number): boolean =>
  now - attempts.lastAttemptAt > SATELLITE_SYNC_EPISODE_GAP_MS;

/**
 * [MCV-1789] One independent bounce counter per `storageKey`. The counters share
 * the same storage layer and semantics but never each other's state: the HLX
 * satellite and the MCV primary count different loops and reset on different
 * signals.
 */
export const createSyncAttemptGuard = (storageKey: string): SyncAttemptGuard => {
  const cookiePattern = new RegExp(`(?:^|; )${escapeRegExp(storageKey)}=([^;]*)`);

  const readCookie = (): string | null => {
    try {
      const match = document.cookie.match(cookiePattern);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };

  const writeCookie = (value: string): void => {
    try {
      document.cookie = `${storageKey}=${encodeURIComponent(
        value
      )}; path=/; max-age=${COOKIE_MAX_AGE_S}; SameSite=Lax`;
    } catch {
      // Nothing left to fall back to; the root fix (Clerk mounted on the satellite)
      // is what actually prevents the loop, so this stays best-effort here.
    }
  };

  const readSession = (): string | null => {
    try {
      return window.sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }
  };

  const writeRaw = (value: string): void => {
    try {
      window.sessionStorage.setItem(storageKey, value);
    } catch {
      writeCookie(value);
    }
  };

  const clearRaw = (): void => {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch {
      // ignore — clear the cookie fallback below regardless.
    }
    try {
      document.cookie = `${storageKey}=; path=/; max-age=0; SameSite=Lax`;
    } catch {
      // best-effort
    }
  };

  // Reconcile BOTH stores rather than reading only one. sessionStorage is primary,
  // but the cookie can hold the newer value when a prior `setItem` threw (a
  // write-only failure under quota/policy) while `getItem` still succeeds — or
  // when sessionStorage retains a stale value after a later write failed. Reading
  // only sessionStorage would then miss the cookie and reset the count to 1,
  // reopening the loop. Return whichever has the newest valid `lastAttemptAt`.
  const readAttempts = (): Attempts | null => {
    const fromSession = parseAttempts(readSession());
    const fromCookie = parseAttempts(readCookie());
    if (!fromSession) return fromCookie;
    if (!fromCookie) return fromSession;
    return fromCookie.lastAttemptAt > fromSession.lastAttemptAt ? fromCookie : fromSession;
  };

  const writeAttempts = (attempts: Attempts): void => {
    writeRaw(JSON.stringify(attempts));
  };

  return {
    record: (now: number = Date.now()): number => {
      const prior = readAttempts();
      const count = prior && !isStale(prior, now) ? prior.count + 1 : 1;
      writeAttempts({ count, lastAttemptAt: now });
      return count;
    },
    isBlocked: (now: number = Date.now()): boolean => {
      const attempts = readAttempts();
      if (!attempts || isStale(attempts, now)) return false;
      return attempts.count >= MAX_SATELLITE_SYNC_ATTEMPTS;
    },
    reset: (): void => {
      clearRaw();
    },
  };
};

// The satellite (HLX) side. Counted in HLX's useSatelliteRedirect and reset by
// AuthStateHook once a satellite session actually establishes.
const satelliteSyncGuard = createSyncAttemptGuard('hlx.satelliteSyncAttempts');

/**
 * Record one satellite -> primary sync bounce and return the running count for
 * the current episode. A bounce that follows a long quiet gap starts a fresh
 * episode at 1.
 */
export const recordSatelliteSyncAttempt = (now?: number): number => satelliteSyncGuard.record(now);

/** True once bounces in the current episode have hit the cap. */
export const isSatelliteSyncBlocked = (now?: number): boolean => satelliteSyncGuard.isBlocked(now);

/** Clear the counter — call once a satellite session actually establishes. */
export const resetSatelliteSyncAttempts = (): void => {
  satelliteSyncGuard.reset();
};

/**
 * [MCV-1789] The primary (MCV) side: counts cross-site hand-offs sent FROM the
 * signed-in primary toward a satellite (`AuthenticatedSignInRoute`). It lives on
 * the PRIMARY origin, so it is a different store from the satellite counter above.
 *
 * It must NOT be reset by `AuthStateHook`, which clears the satellite counter on
 * every signed-in load: the primary is signed in for the whole of this loop, so a
 * reset there would zero the count on each bounce and the cap would never trip.
 * Only the unavailable page's own retry button, or the episode gap, clears it.
 */
export const satelliteHandoffGuard = createSyncAttemptGuard('mcv.satelliteHandoffAttempts');
